// src/pages/ScoreCommentCreate.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Editor } from "@tinymce/tinymce-react";

import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";

export default function ScoreCommentCreate() {
  const { testId, categoryId } = useParams();
  const nav = useNavigate();
  const [testName, setTestName] = useState("");
  const [categoryName, setCategoryName] = useState("");
  
  const token = localStorage.getItem("admin_token");
  const base  = process.env.REACT_APP_API_BASE_URL;

  // ⬇️ Yardımcılar
  const pickName = (obj = {}) =>
    obj?.name ?? obj?.title ?? obj?.category_name ?? obj?.label ?? "";

  const normalizeArray = (data) =>
    Array.isArray(data) ? data
    : Array.isArray(data?.data) ? data.data
    : Array.isArray(data?.rows) ? data.rows
    : [];
  /* i18n */
  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (k, d = "") => translations[k] || d;

  /* ▸ form state */
  const [form, setForm] = useState({
    min_score: 0,
    max_score: "",
    summary: "",
    details: ""
  });
  const [loading, setLoading] = useState(false);
  const [minFloor, setMinFloor] = useState(0);
  const [testMaxScore, setTestMaxScore] = useState(null);

  /* clamp ve state güncelleme */
  const handle = e => {
    let { name, value } = e.target;
    if (name === "max_score" && testMaxScore != null) {
      const num = Number(value);
      if (!isNaN(num) && num > testMaxScore) {
        value = String(testMaxScore);
      }
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };
  useEffect(() => {
    (async () => {
      try {
        // Test adı
        try {
          const { data: test } = await axios.get(`${base}/api/tests/${testId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setTestName(pickName(test) || `Test #${testId}`);
        } catch {
          setTestName(`Test #${testId}`);
        }

        // Kategori yoksa çık
        if (!categoryId) {
          setCategoryName("");
          return;
        }

        let name = "";

        // 1) /api/categories/:id
        try {
          const { data } = await axios.get(`${base}/api/categories/${categoryId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          name = pickName(data) || name;
        } catch {}

        // 2) /api/test-categories/:id
        if (!name) {
          try {
            const { data } = await axios.get(`${base}/api/test-categories/${categoryId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            name = pickName(data) || name;
          } catch {}
        }

        // 3) /api/tests/:testId/categories içinde ara
        if (!name) {
          try {
            const { data } = await axios.get(`${base}/api/tests/${testId}/categories`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const list = normalizeArray(data);
            const found = list.find(
              (c) =>
                String(c.id) === String(categoryId) ||
                String(c.category_id) === String(categoryId)
            );
            name = pickName(found) || name;
          } catch {}
        }

        setCategoryName(name || `Kategori #${categoryId}`);
      } catch {
        // sessiz geç
      }
    })();
  }, [testId, categoryId, base, token]);

 useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("admin_token");
        const baseUrl = process.env.REACT_APP_API_BASE_URL;

        // 1) Kategoriye özel yorumları çek
        const commentsUrl = categoryId
          ? `${baseUrl}/api/score-comments/tests/${testId}/category/${categoryId}/score-comments`
          : `${baseUrl}/api/score-comments/tests/${testId}`;
        const respComments = await axios.get(commentsUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list =
          Array.isArray(respComments.data) ? respComments.data :
          Array.isArray(respComments.data.data) ? respComments.data.data :
          Array.isArray(respComments.data.rows) ? respComments.data.rows :
          [];
        if (list.length) {
          const lastMax = Math.max(...list.map(r => r.max_score));
          const nextMin = testMaxScore != null
            ? Math.min(lastMax + 1, testMaxScore)
            : lastMax + 1;
          setMinFloor(nextMin);
          setForm(f => ({ ...f, min_score: nextMin }));
        }

        // 2) Test detayını çekip maksimum puanı al
        const respTest = await axios.get(
          `${baseUrl}/api/tests/${testId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTestMaxScore(
          respTest.data.max_score ??
          respTest.data.total_score ??
          null
        );
      } catch (err) {
        console.error("❌ Veri yüklenirken hata:", err);
      }
    })();
  }, [testId, categoryId, testMaxScore]);

  /* ▸ submit */
  const save = async e => {
    e.preventDefault();

    const min = +form.min_score;
    const max = +form.max_score;

    if (isNaN(min) || isNaN(max))
      return toast.error(t("ERR_REQ", "Puanlar sayısal olmalı"));
    if (max < min)
      return toast.error(t("ERR_RANGE", "Maksimum puan minimumdan küçük olamaz"));
    if (testMaxScore != null && max > testMaxScore)
      return toast.error(
        t("ERR_MAX_EXCEED", "Maksimum puan testin izin verdiği puanı geçemez")
      );

    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/score-comments`,
        {
          test_id: testId,
          category_id: categoryId || null,
          min_score: min,
          max_score: max,
          summary: form.summary.trim(),
          details: form.details.trim()
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }
      );
    
        toast.success("Puan açıklaması başarıyla oluşturuldu!");
      setTimeout(() => {
  if (categoryId) {
    // Kategori varsa kategoriye özel puan açıklaması listesine dön
    nav(`/test/${testId}/category/${categoryId}/score/table`);
  } else {
    // Kategori yoksa genel test puan açıklaması listesine dön
    nav(`/test/score/table/${testId}`);
  }
}, 1000);

    } catch (err) {
      console.error("❌ Kayıt sırasında hata:", err);
      toast.error(t("ERROR_CREATE", "Kayıt sırasında hata oluştu"));
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <div className="container mt-4">
       <ToastContainer />
       {/* ▸ Context line */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="small text-muted">
          {categoryId ? (
            <>
              Test: <strong>{testName }</strong>
              {" · "}
              Kategori: <strong>{categoryName }</strong>
            </>
          ) : (
            <>
              Test: <strong>{testName}</strong>
            </>
          )}
        </div>
      </div>
      <form className="card p-4" onSubmit={save}>
        <h5 className="mb-3 fw-bold">
          {t("CREATE_SCORE_COMMENT", "Puan Açıklaması Ekle")}
        </h5>

        {/* Puan Aralığı */}
        <div className="mb-3">
          <label className="form-label">Puan Aralığı</label>
          <div className="d-flex align-items-center gap-2">
            <input
              name="min_score"
              type="number"
              className="form-control"
              style={{ maxWidth: 120 }}
              value={form.min_score}
              min={minFloor}
              max={testMaxScore ?? undefined}
              step={1}
              onChange={handle}
              disabled={loading}
              required
            />
            <span className="fw-bold">-</span>
            <input
              name="max_score"
              type="number"
              className="form-control"
              style={{ maxWidth: 120 }}
              value={form.max_score}
              min={form.min_score}
              max={testMaxScore ?? undefined}
              step={1}
              onChange={handle}
              disabled={loading}
              required
            />
          </div>
          {minFloor > 0 && (
            <small className="text-muted">
              En düşük izin verilen puan: {minFloor}
            </small>
          )}
          {testMaxScore != null && (
            <small className="text-muted d-block">
              Bu testin en yüksek puanı: <strong>{testMaxScore}</strong>
            </small>
          )}
        </div>

        {/* Özet */}
        <div className="mb-3">
          <label className="form-label fw-bold">
            {t("TABLE_SUMMARY", "Özet")}
          </label>
          <Editor
            tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
            value={form.summary}
            onEditorChange={content =>
              setForm(prev => ({ ...prev, summary: content }))
            }
            init={{
              promotion: false,
              height: 400,
              menubar: true,
              plugins: [
                "advlist autolink lists link image charmap preview anchor",
                "searchreplace visualblocks code fullscreen",
                "insertdatetime media table emoticons help wordcount"
              ].join(" "),
              toolbar:
                "undo redo | formatselect | bold italic underline strikethrough | " +
                "alignleft aligncenter alignright alignjustify | " +
                "bullist numlist outdent indent | link image media table | " +
                "fullscreen preview code help",
              content_style:
                "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
              automatic_uploads: false,
              images_upload_handler: (blobInfo, success, failure) => {
                const fd = new FormData();
                fd.append("file", blobInfo.blob(), blobInfo.filename());
                fetch(
                  `${process.env.REACT_APP_API_BASE_URL}/api/score-comments/upload-image`,
                  { method: "POST", body: fd }
                )
                  .then(r => r.json())
                  .then(json => {
                    const url = json.location;
                    const img = new Image();
                    img.onload = () => {
                      const html = `<img 
                        src="${url}"
                        width="${img.naturalWidth}"
                        height="${img.naturalHeight}"
                        style="max-width:100%;height:auto;"
                      />`;
                      window.tinymce.activeEditor.insertContent(html);
                      success(url);
                    };
                    img.onerror = () => failure("Boyut okunamadı");
                    img.src = url;
                  })
                  .catch(err => failure(err.message));
              },
              images_upload_tab: true,
              image_advtab: true,
              file_picker_types: "image",
              relative_urls: false,
              remove_script_host: false,
              convert_urls: true
            }}
          />
        </div>

        {/* Detay */}
        <div className="mb-3">
          <label className="form-label fw-bold">{t("DETAILS", "Detay")}</label>
          <Editor
            tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
            value={form.details}
            onEditorChange={content =>
              setForm(prev => ({ ...prev, details: content }))
            }
            init={{
              promotion: false,
              height: 600,
              menubar: true,
              plugins: [
                "advlist autolink lists link image charmap preview anchor",
                "searchreplace visualblocks code fullscreen",
                "insertdatetime media table emoticons help wordcount"
              ].join(" "),
              toolbar:
                "undo redo | formatselect | bold italic underline strikethrough | " +
                "alignleft aligncenter alignright alignjustify | " +
                "bullist numlist outdent indent | link image media table | " +
                "fullscreen preview code help",
              content_style:
                "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
              automatic_uploads: true,
              images_upload_tab: true,
              images_upload_url:
                `${process.env.REACT_APP_API_BASE_URL}/api/score-comments/upload-image`,
              images_upload_handler: null,
              image_advtab: true
            }}
          />
        </div>

        {/* Kaydet */}
        <div className="d-flex justify-content-center mt-4">
          <button className="btn btn-primary px-4" disabled={loading}>
            {loading ? t("LOADING", "Kaydediliyor…") : t("SAVE", "Kaydet")}
          </button>
        </div>
      </form>
    </div>
  );
}
